import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import DocumentViewerModal from '../../tournaments/components/DocumentViewerModal';

/**
 * The terms and waiver a payer has to accept, and the name that signs them.
 *
 * Told what to ask, like the questions step: the caller hands over what this
 * offering requires and gets back what was accepted. A component that knew
 * which offerings need a waiver would need editing every time one did.
 *
 * ─── HOW THE DOCUMENT IS READ ───────────────────────────────────────────────
 *
 * The same way the tournament registration form does it: the sentence reads
 * normally and the document is a link inside it, opening the shared viewer over
 * the page. An accordion embedded in the step pushed the Pay button off screen
 * and made the agreement look like part of the form rather than a document
 * being agreed to.
 *
 * DocumentViewerModal takes a docPath, so it serves any offering — it is not
 * tournament-specific despite where it lives.
 *
 * ─── WHY THE NAME MATTERS ───────────────────────────────────────────────────
 *
 * The typed name IS the signature — it is what makes the record evidence that
 * a person agreed rather than a box that happened to be ticked. The server
 * refuses an acceptance without one, and so does this.
 */
export function agreementsSatisfied(agreement, state) {
  if (!agreement) return true;
  const needsAny = agreement.requireTerms || agreement.requireWaiver;
  if (!needsAny) return true;
  if (agreement.requireTerms && !state.termsAccepted) return false;
  if (agreement.requireWaiver && !state.waiverAccepted) return false;
  return String(state.acceptedName || '').trim().length > 1;
}

export default function PaymentAgreements({
  agreement,
  value = {},
  onChange,
}) {
  const { theme } = useTheme();
  // 'terms' | 'waiver' | null — which document is open over the page.
  //
  // Declared BEFORE the early return. A hook after a conditional return runs on
  // some renders and not others, and React counts hooks by position: the render
  // where the agreement arrives would call one more than the render before it,
  // and the whole tree comes down with "Rendered more hooks than during the
  // previous render".
  const [viewer, setViewer] = useState(null);

  if (!agreement || (!agreement.requireTerms && !agreement.requireWaiver)) return null;

  const set = (patch) => onChange?.({ ...value, ...patch });
  // The URL to fetch, not the storage path. The server resolves which endpoint
  // serves this offering's documents, so this component never has to know.
  const paths = { terms: agreement.termsUrl, waiver: agreement.waiverUrl };

  const DocLink = ({ which, children }) => (
    paths[which] ? (
      <button
        type="button"
        onClick={(e) => {
          // Reading a document is not agreeing to it. Without this the click
          // bubbles to the surrounding label and ticks the box on the way to
          // opening the very text it claims to have read.
          e.preventDefault();
          e.stopPropagation();
          setViewer(which);
        }}
        data-testid={`agree-read-${which}`}
        className="text-blue-500 hover:text-blue-600 underline font-medium"
      >
        {children}
      </button>
    ) : (
      <span className="font-medium">{children}</span>
    )
  );

  return (
    <div className="space-y-4">
      {viewer && (
        <DocumentViewerModal
          type={viewer}
          docPath={paths[viewer]}
          onClose={() => setViewer(null)}
        />
      )}

      {/* gap, not space-x. Tailwind v4 emits space-x inside :where(), which has
          zero specificity, so Preflight's `margin: 0` on form elements beats it —
          space-x silently does nothing whenever the first child is an input. gap
          is a container property and nothing can reset it away. */}
      {/* A label, so the whole sentence toggles the box — a 16px target for
          something mandatory is a needless miss. The document link inside it
          stops the click from reaching the label, or opening the terms would
          also tick the box agreeing to them, which is not the same act. */}
      {agreement.requireTerms && (
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value.termsAccepted}
            onChange={e => set({ termsAccepted: e.target.checked })}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
            data-testid="agree-terms"
          />
          <span className={`text-sm ${theme.text}`}>
            I agree to the <DocLink which="terms">Terms &amp; Conditions</DocLink>,
            including rules, regulations, and code of conduct. *
          </span>
        </label>
      )}

      {agreement.requireWaiver && (
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value.waiverAccepted}
            onChange={e => set({ waiverAccepted: e.target.checked })}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
            data-testid="agree-waiver"
          />
          <span className={`text-sm ${theme.text}`}>
            I acknowledge and accept the <DocLink which="waiver">Liability Waiver</DocLink>{' '}
            and understand the risks involved in taking part. *
          </span>
        </label>
      )}

      <div>
        <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
          Type your name to sign <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={value.acceptedName || ''}
          onChange={e => set({ acceptedName: e.target.value })}
          placeholder="Your full name"
          data-testid="agree-name"
          className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input || ''} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm`}
        />
        {/* Said plainly. A record with no name behind it is not evidence that
            anybody agreed to anything. */}
        <p className={`text-xs mt-1 ${theme.textMuted}`}>
          Your name is your signature, and is recorded with the version you accepted.
        </p>
      </div>
    </div>
  );
}
